import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, MessageCircle, LogOut, Calendar, Search, ArrowLeft, FileText, Heart, Settings, ShieldCheck, ClipboardList, User, History, Wallet, Home, Compass, MapPin, Users, Briefcase, Smartphone, Bell, Mail, LifeBuoy, Star, Lightbulb, Crown, Bug } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNotificationsData } from "@/hooks/useNotificationsData";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { useFavorites } from "@/hooks/useFavorites";
import NotificationBell from "@/components/NotificationBell";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";


const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, role, signOut } = useAuth();
  const { unreadCount } = useNotificationsData();
  const { unreadMessages } = useUnreadMessages();
  const { favoriteIds } = useFavorites();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <nav
      className="sticky top-0 z-[500] w-full border-b border-white/20 shadow-neon bg-cg-animated"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        // Ensure the navbar always receives pointer events even when a Radix
        // modal (e.g. ProfileQuickView on the public profile page) sets
        // `pointer-events: none` on the body outside its portal.
        pointerEvents: "auto",
      }}
    >
      <div className="w-full px-2 md:px-3 lg:px-4 flex items-center justify-between h-16 gap-2">
        <div className="flex items-center gap-2 shrink-0">
          {!isHome && (
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 rounded-xl hover:bg-white/15 transition-all duration-200"
              aria-label="Retour"
            >
              <ArrowLeft className="w-5 h-5 icon-led-white" />
            </button>
          )}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-xl overflow-hidden flex items-center justify-center shadow-neon group-hover:glow-violet transition-all duration-300 group-hover:scale-105 ring-2 ring-white/40">
              <img alt="Click&Gard" className="w-9 h-9 object-cover" src="/lovable-uploads/2236e7c0-4083-436f-a95c-06d4486b46d6.png" />
            </div>
            <span className="text-base font-bold hidden 2xl:inline tracking-tight font-display text-led-white">
              Click&Gard
            </span>
          </Link>
        </div>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-0.5 flex-1 justify-end min-w-0">
          {user ? (
            <>
              <NavItem to="/" icon={Home} label="Accueil" />
              <DiscoverDropdown />
              <NavItem to="/search" icon={Search} label="Rechercher" />
              {role === "pro" && <NavItem to="/pro/dashboard" icon={Calendar} label="Planning" />}
              {role === "pro" && <NavItem to="/pro/reservations" icon={ClipboardList} label="Réservations" />}
              {role === "pro" && <NavItem to="/mes-gains" icon={Wallet} label="Mes gains" />}
              {(role === "pro" || role === "admin") && <NavItem to="/subscription" icon={Crown} label="Abonnement" />}
              {role === "parent" && <NavItem to="/favorites" icon={Heart} label="Favoris" badge={favoriteIds.size} />}
              {role !== "pro" && <NavItem to="/mes-reservations" icon={ClipboardList} label="Réservations" />}
              <NavItem to="/historique" icon={History} label="Historique" />
              <NavItem to="/demandes" icon={FileText} label="Annonces" />
              <NavItem to="/messages" icon={MessageCircle} label="Messages" badge={unreadMessages} />
              <NavItem to="/signaler-un-bug" icon={Bug} label="Signaler un bug" />
              <NavItem to="/mon-profil" icon={User} label="Profil" />
              <NavItem to="/settings" icon={Settings} label="Paramètres" />
              {role === "admin" && <NavItem to="/admin" icon={ShieldCheck} label="Admin" />}
              <div className="ml-2 flex items-center gap-1">
                <NotificationBell />
                <Button variant="ghost" size="sm" onClick={handleSignOut} className="rounded-xl hover:bg-white/15">
                  <LogOut className="w-4 h-4 icon-led-white" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <DiscoverDropdown />
              <Link to="/auth">
                <Button variant="ghost" size="sm" className="rounded-xl font-semibold font-display text-led-white hover:bg-white/15">Connexion</Button>
              </Link>
              <Link to="/auth?tab=register">
                <Button size="sm" className="rounded-xl font-semibold font-display text-led-white shadow-neon bg-white/15 hover:bg-white/25 ring-1 ring-white/40 backdrop-blur-sm">Inscription</Button>
              </Link>
            </div>
          )}
        </div>

        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <button className="md:hidden p-2 rounded-xl hover:bg-white/15 transition-colors" aria-label="Ouvrir le menu">
              {isOpen ? <X className="w-5 h-5 icon-led-white" /> : <Menu className="w-5 h-5 icon-led-white" />}
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-[85vw] max-w-sm p-0 flex flex-col bg-background z-[600] overscroll-contain"
          >
            <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-1">
              <MobileLink to="/" label="Accueil" onClick={() => setIsOpen(false)} />
              {user && (
                <Link
                  to="/notifications"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between gap-2 px-3 py-3 rounded-xl bg-primary/10 hover:bg-primary/15 transition-colors"
                >
                  <span className="flex items-center gap-2 text-base font-semibold text-primary">
                    <Bell className="w-5 h-5" />
                    Notifications
                  </span>
                  {unreadCount > 0 && (
                    <span className="min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-xs font-bold">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </Link>
              )}
              <div className="pt-2 pb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Découvrir</div>
              <MobileLink to="/babysitter-france" label="Babysitter en France" onClick={() => setIsOpen(false)} />
              <MobileLink to="/comment-ca-marche-parents" label="Comment ça marche – Parents" onClick={() => setIsOpen(false)} />
              <MobileLink to="/comment-ca-marche-pros" label="Comment ça marche – Pros" onClick={() => setIsOpen(false)} />
              <MobileLink to="/install" label="📱 Installer l'application" onClick={() => setIsOpen(false)} />
              <MobileLink to="/contact" label="✉️ Nous contacter" onClick={() => setIsOpen(false)} />
              {user && <MobileLink to="/support" label="🛟 Support technique (chat)" onClick={() => setIsOpen(false)} />}
              <MobileLink to="/avis-support" label="⭐ Avis support technique" onClick={() => setIsOpen(false)} />
              <MobileLink to="/suggestions" label="💡 Suggestions" onClick={() => setIsOpen(false)} />
              <MobileLink to="/avis" label="⭐ Avis sur Click&Gard" onClick={() => setIsOpen(false)} />
              <div className="border-t border-border my-2" />
              {user ? (
                <>
                  <MobileLink to="/search" label="Rechercher" onClick={() => setIsOpen(false)} />
                  {role === "pro" && <MobileLink to="/pro/dashboard" label="Mon planning" onClick={() => setIsOpen(false)} />}
                  {role === "pro" && <MobileLink to="/pro/reservations" label="Mes réservations" onClick={() => setIsOpen(false)} />}
                  {role === "pro" && <MobileLink to="/mes-gains" label="Mes gains" onClick={() => setIsOpen(false)} />}
                  {(role === "pro" || role === "admin") && <MobileLink to="/subscription" label="👑 Mon abonnement" onClick={() => setIsOpen(false)} />}
                  {role === "parent" && <MobileLink to="/favorites" label={`❤️ Mes favoris${favoriteIds.size ? ` (${favoriteIds.size})` : ""}`} onClick={() => setIsOpen(false)} />}
                  {role !== "pro" && <MobileLink to="/mes-reservations" label="Mes réservations" onClick={() => setIsOpen(false)} />}
                  <MobileLink to="/historique" label="Historique" onClick={() => setIsOpen(false)} />
                  <MobileLink to="/demandes" label="Annonces" onClick={() => setIsOpen(false)} />
                  <MobileLink to="/signaler-un-bug" label="🐞 Signaler un bug" onClick={() => setIsOpen(false)} />
                  <Link
                    to="/messages"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center justify-between gap-2 px-3 py-3 rounded-xl hover:bg-muted transition-colors"
                  >
                    <span className="text-base font-semibold text-foreground">Messages</span>
                    {unreadMessages > 0 && (
                      <span className="min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-xs font-bold">
                        {unreadMessages > 99 ? "99+" : unreadMessages}
                      </span>
                    )}
                  </Link>
                  <MobileLink to="/mon-profil" label="Mon profil" onClick={() => setIsOpen(false)} />
                  <MobileLink to="/settings" label="Paramètres" onClick={() => setIsOpen(false)} />
                  {role === "admin" && <MobileLink to="/admin" label="Administration" onClick={() => setIsOpen(false)} />}
                  <div className="flex items-center gap-2 pt-3 border-t border-border">
                    <NotificationBell />
                    <Button variant="ghost" size="sm" className="rounded-xl text-foreground hover:bg-muted" onClick={() => { handleSignOut(); setIsOpen(false); }}>
                      Déconnexion
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex gap-2 pt-3">
                  <Link to="/auth" className="flex-1" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full rounded-xl" size="sm">Connexion</Button>
                  </Link>
                  <Link to="/auth?tab=register" className="flex-1" onClick={() => setIsOpen(false)}>
                    <Button className="w-full rounded-xl bg-gradient-to-r from-primary to-secondary shadow-neon" size="sm">Inscription</Button>
                  </Link>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>

    </nav>
  );
};

const NavItem = ({ to, icon: Icon, label, badge }: { to: string; icon: LucideIcon; label: string; badge?: number }) => (
  <Link
    to={to}
    title={label}
    className="group relative flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-lg hover:bg-white/15 transition-all duration-200 whitespace-nowrap"
  >
    {Icon && <Icon className="w-5 h-5 shrink-0 icon-led-white" />}
    {badge != null && badge > 0 && (
      <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none">
        {badge > 99 ? "99+" : badge}
      </span>
    )}
    <span className="text-[11px] font-semibold leading-tight font-display tracking-wide text-led-white">
      {label}
    </span>
  </Link>
);

const DiscoverDropdown = () => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        title="Découvrir"
        className="group flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-lg hover:bg-white/15 transition-all duration-200 whitespace-nowrap focus:outline-none"
      >
        <Compass className="w-5 h-5 shrink-0 icon-led-white" />
        <span className="text-[11px] font-semibold leading-tight font-display tracking-wide text-led-white">
          Découvrir
        </span>
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-64 z-[600]">
      <DropdownMenuItem asChild>
        <Link to="/babysitter-france" className="cursor-pointer gap-2">
          <MapPin className="w-4 h-4 text-primary" />
          <div className="flex flex-col">
            <span className="font-semibold">Babysitter en France</span>
            <span className="text-xs text-muted-foreground">Page nationale</span>
          </div>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/comment-ca-marche-parents" className="cursor-pointer gap-2">
          <Users className="w-4 h-4 text-primary" />
          <div className="flex flex-col">
            <span className="font-semibold">Comment ça marche</span>
            <span className="text-xs text-muted-foreground">Pour les parents</span>
          </div>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/comment-ca-marche-pros" className="cursor-pointer gap-2">
          <Briefcase className="w-4 h-4 text-secondary" />
          <div className="flex flex-col">
            <span className="font-semibold">Comment ça marche</span>
            <span className="text-xs text-muted-foreground">Pour les baby-sitters</span>
          </div>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/install" className="cursor-pointer gap-2">
          <Smartphone className="w-4 h-4 text-primary" />
          <div className="flex flex-col">
            <span className="font-semibold">Installer l'application</span>
            <span className="text-xs text-muted-foreground">Sur iPhone, Android ou ordinateur</span>
          </div>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/contact" className="cursor-pointer gap-2">
          <Mail className="w-4 h-4 text-secondary" />
          <div className="flex flex-col">
            <span className="font-semibold">Nous contacter</span>
            <span className="text-xs text-muted-foreground">Une question ? Écrivez-nous</span>
          </div>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/support" className="cursor-pointer gap-2">
          <LifeBuoy className="w-4 h-4 text-primary" />
          <div className="flex flex-col">
            <span className="font-semibold">Support technique</span>
            <span className="text-xs text-muted-foreground">Chat direct avec notre équipe</span>
          </div>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/avis-support" className="cursor-pointer gap-2">
          <Star className="w-4 h-4 text-secondary" />
          <div className="flex flex-col">
            <span className="font-semibold">Avis support technique</span>
            <span className="text-xs text-muted-foreground">Évaluations utilisateurs</span>
          </div>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/suggestions" className="cursor-pointer gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <div className="flex flex-col">
            <span className="font-semibold">💡 Suggestions</span>
            <span className="text-xs text-muted-foreground">Proposez vos idées d'amélioration</span>
          </div>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/avis" className="cursor-pointer gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          <div className="flex flex-col">
            <span className="font-semibold">⭐ Avis sur Click&Gard</span>
            <span className="text-xs text-muted-foreground">Notez la plateforme</span>
          </div>
        </Link>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const MobileLink = ({ to, label, onClick }: { to: string; label: string; onClick: () => void }) => (
  <Link
    to={to}
    className="block text-base font-semibold text-foreground hover:bg-muted hover:text-primary px-3 py-3 rounded-xl transition-colors"
    onClick={onClick}
  >
    {label}
  </Link>
);

export default Navbar;
